import type {MockQuestionnaire, MockQuestion} from "../../types";

const gitcQuestions: MockQuestion[] = [
  {
    id: "eq1-q1",
    text: "Are logical access controls in place for all critical IT systems?",
    type: "yes-no",
    answer: "Yes",
    note: "Confirmed via walkthrough of Active Directory and SSO configurations.",
  },
  {
    id: "eq1-q2",
    text: "Describe the process for granting, modifying, and revoking user access.",
    type: "text",
    answer:
      "Access requests are submitted through ServiceNow, approved by the system owner and IT security, then provisioned by the IT admin team. Revocation occurs automatically upon termination via HR system integration.",
  },
  {
    id: "eq1-q3",
    text: "How frequently are user access reviews performed?",
    type: "select",
    options: ["Monthly", "Quarterly", "Semi-annually", "Annually"],
    answer: "Quarterly",
  },
  {
    id: "eq1-q4",
    text: "Is multi-factor authentication (MFA) enforced for all privileged accounts?",
    type: "yes-no",
    answer: "Yes",
  },
  {
    id: "eq1-q5",
    text: "Which identity provider(s) are used for centralized authentication?",
    type: "multi-select",
    options: ["Azure AD / Entra ID", "Okta", "Google Workspace", "On-premise AD", "Other"],
    answer: ["Azure AD / Entra ID", "Okta"],
  },
  {
    id: "eq1-q6",
    text: "Which change management controls are in place?",
    type: "multi-select",
    options: ["Peer code review", "Segregation of duties", "CAB approval", "Automated testing", "Rollback procedures"],
    answer: ["Peer code review", "CAB approval", "Automated testing", "Rollback procedures"],
  },
  {
    id: "eq1-q7",
    text: "Are backup and recovery procedures documented and tested regularly?",
    type: "yes-no",
    answer: "Yes",
    note: "Annual disaster recovery test completed in October 2025.",
  },
  {
    id: "eq1-q8",
    text: "How are terminated employee accounts disabled?",
    type: "select",
    options: [
      "Automated via HR integration",
      "Manual process within 24 hours",
      "Manual process within 1 week",
      "Ad hoc",
    ],
    answer: "Automated via HR integration",
  },
];

const ceBoardWalkthroughQuestions: MockQuestion[] = [
  {
    id: "ce-iq1-q1",
    text: "Has the audit committee charter been reviewed and updated within the last 12 months?",
    type: "yes-no",
    answer: "Yes",
    note: "Last approved by the board in January 2026.",
  },
  {
    id: "ce-iq1-q2",
    text: "What reports does the audit committee receive from management at each meeting?",
    type: "text",
    answer:
      "Financial performance summary, internal audit status report, open deficiency tracker, regulatory correspondence log, and fraud/ethics hotline summary.",
  },
  {
    id: "ce-iq1-q3",
    text: "How does the board communicate expectations regarding management override of controls?",
    type: "text",
    answer:
      "The board's code of conduct policy explicitly prohibits management override. The audit committee chair reinforces this expectation at the start of each quarterly meeting.",
  },
  {
    id: "ce-iq1-q4",
    text: "Are board and committee meeting minutes documented and retained?",
    type: "yes-no",
    answer: "Yes",
    note: "Minutes are drafted by the corporate secretary and approved at the subsequent meeting.",
  },
  {
    id: "ce-iq1-q5",
    text: "Rate the overall maturity of board oversight and governance.",
    type: "select",
    options: ["Developing", "Established", "Mature"],
    answer: "Established",
    note: "Strong committee structure; documentation of follow-up actions could be improved.",
  },
];

const ceEnvironmentQuestions: MockQuestion[] = [
  {
    id: "ce-iq2-q1",
    text: "Is there a formal Code of Conduct that covers financial reporting integrity?",
    type: "yes-no",
    answer: "Yes",
    note: "Code of Conduct v4.0, effective January 2026.",
  },
  {
    id: "ce-iq2-q2",
    text: "How frequently are employees required to acknowledge the Code of Conduct?",
    type: "select",
    options: ["Upon hire only", "Annually", "Bi-annually", "No formal requirement"],
    answer: "Annually",
  },
  {
    id: "ce-iq2-q3",
    text: "What mechanisms exist for employees to report ethical concerns?",
    type: "multi-select",
    options: [
      "Anonymous hotline",
      "Direct manager reporting",
      "HR department",
      "Ethics committee",
      "External ombudsman",
    ],
    answer: ["Anonymous hotline", "Direct manager reporting", "HR department"],
  },
  {
    id: "ce-iq2-q4",
    text: "Are competence requirements documented for key financial reporting roles?",
    type: "yes-no",
    answer: "Yes",
    note: "Job descriptions specify required qualifications, certifications, and experience levels.",
  },
  {
    id: "ce-iq2-q5",
    text: "Is there a formal succession plan for critical financial reporting positions?",
    type: "yes-no",
    answer: "Yes",
    note: "Succession plans reviewed annually by the CFO for Controller, Head of Tax, and Treasury Manager.",
  },
  {
    id: "ce-iq2-q6",
    text: "Describe the annual performance evaluation process for finance staff.",
    type: "text",
    answer:
      "Annual evaluations include assessment of technical competence, adherence to internal controls, and ethical standards. Results inform CPD plans and promotion decisions.",
  },
];

const mocMonitoringEffectivenessQuestions: MockQuestion[] = [
  {
    id: "moc-iq1-q1",
    text: "What key performance indicators are monitored to assess control effectiveness?",
    type: "text",
    answer:
      "Transaction processing accuracy rates, reconciliation completion timeliness, exception volumes by category, and control execution timeliness against SLAs.",
  },
  {
    id: "moc-iq1-q2",
    text: "How frequently is the KPI dashboard reviewed by management?",
    type: "select",
    options: ["Weekly", "Monthly", "Quarterly", "Ad hoc"],
    answer: "Monthly",
    note: "The Controller reviews the dashboard monthly with escalation for adverse trends.",
  },
  {
    id: "moc-iq1-q3",
    text: "Are thresholds defined for when KPI deviations trigger investigation?",
    type: "yes-no",
    answer: "Yes",
    note: "Thresholds set during initial configuration; not formally reviewed since implementation.",
  },
  {
    id: "moc-iq1-q4",
    text: "Describe the escalation process when monitoring identifies a potential control breakdown.",
    type: "text",
    answer:
      "Exceptions are routed to the relevant control owner for investigation within 24 hours. Unresolved items are escalated to the Controller at the next monthly review. Critical items are escalated to the CFO immediately.",
  },
  {
    id: "moc-iq1-q5",
    text: "How frequently are automated exception monitoring rules reviewed and updated?",
    type: "select",
    options: ["Quarterly", "Semi-annually", "Annually", "Ad hoc only"],
    answer: "Ad hoc only",
    note: "No formal periodic review schedule exists. Rules updated when issues are identified.",
  },
];

const mocDeficiencyTrackingQuestions: MockQuestion[] = [
  {
    id: "moc-iq2-q1",
    text: "What system or tool is used to track identified deficiencies and remediation actions?",
    type: "select",
    options: ["Automated GRC platform", "Spreadsheet", "Ticketing system", "No formal system"],
    answer: "Spreadsheet",
    note: "Currently a shared Excel workbook; automated tool procurement underway.",
  },
  {
    id: "moc-iq2-q2",
    text: "Are remediation SLAs defined for each deficiency severity level?",
    type: "yes-no",
    answer: "Yes",
    note: "Critical: 30 days, Moderate: 60 days, Low: 90 days.",
  },
  {
    id: "moc-iq2-q3",
    text: "Describe the escalation process when remediation SLAs are not met.",
    type: "text",
    answer: null,
  },
  {
    id: "moc-iq2-q4",
    text: "Are remediated deficiencies subject to re-testing before closure?",
    type: "yes-no",
    answer: "Yes",
    note: "Internal audit performs re-testing for moderate and above deficiencies.",
  },
  {
    id: "moc-iq2-q5",
    text: "How frequently is the deficiency status reported to the audit committee?",
    type: "select",
    options: ["Monthly", "Quarterly", "Semi-annually", "Annually"],
    answer: null,
  },
];

const soxControlsQuestions: MockQuestion[] = [
  {
    id: "sox-q1",
    text: "Are segregation of duties controls enforced between invoice entry and payment approval?",
    type: "yes-no",
    answer: "Yes",
    note: "System-enforced in SAP — same user cannot enter and approve the same invoice.",
  },
  {
    id: "sox-q2",
    text: "Which controls are in place to prevent unauthorised vendor master data changes?",
    type: "multi-select",
    options: [
      "Restricted access to vendor master",
      "Dual approval for new vendors",
      "Periodic vendor master review",
      "System logging of all changes",
    ],
    answer: ["Restricted access to vendor master", "Dual approval for new vendors", "System logging of all changes"],
  },
  {
    id: "sox-q3",
    text: "How is the three-way match tolerance configured?",
    type: "select",
    options: ["No tolerance (exact match)", "Within 2%", "Within 5%", "Within 10%"],
    answer: "Within 5%",
    note: "Exceptions above 5% require AP supervisor review.",
  },
  {
    id: "sox-q4",
    text: "Is there a periodic review of user access rights within the AP module?",
    type: "yes-no",
    answer: "Yes",
    note: "Quarterly access review performed by IT Security.",
  },
  {
    id: "sox-q5",
    text: "Describe the process for handling duplicate invoice detection.",
    type: "text",
    answer:
      "SAP automatically flags potential duplicates based on vendor number, invoice number, amount, and date. Flagged items are routed to the AP supervisor for manual review before posting.",
  },
];

const jeControlTestingQuestions: MockQuestion[] = [
  {
    id: "je-ct-q1",
    text: "Is dual approval enforced for all manual journal entries above the materiality threshold?",
    type: "yes-no",
    answer: "Yes",
    note: "System-enforced — entries above €10,000 require Controller approval.",
  },
  {
    id: "je-ct-q2",
    text: "Which automated controls exist for journal entry posting?",
    type: "multi-select",
    options: [
      "Account code validation",
      "Period posting restrictions",
      "Debit/credit balance check",
      "Duplicate entry detection",
      "Threshold-based routing",
    ],
    answer: [
      "Account code validation",
      "Period posting restrictions",
      "Debit/credit balance check",
      "Threshold-based routing",
    ],
  },
  {
    id: "je-ct-q3",
    text: "How are non-standard or unusual journal entries identified and reviewed?",
    type: "text",
    answer:
      "The system flags entries to unusual account combinations, entries posted outside business hours, and entries by users who do not regularly post. These are included in the monthly Controller review.",
  },
  {
    id: "je-ct-q4",
    text: "Are there restrictions on who can post journal entries during the close period?",
    type: "yes-no",
    answer: "Yes",
    note: "Only senior accountants and above can post during the close window (last 3 business days of the month).",
  },
];

const gitcControlTestingQuestions: MockQuestion[] = [
  {
    id: "gitc-ct-q1",
    text: "Has GITC-01 (User Access Provisioning) been tested for operating effectiveness?",
    type: "yes-no",
    answer: "Yes",
    note: "Sample of 25 new starters tested — all had appropriate approval documentation.",
  },
  {
    id: "gitc-ct-q2",
    text: "What exceptions were identified during GITC-02 (Quarterly Access Review) testing?",
    type: "text",
    answer:
      "Two exceptions identified: one terminated employee account remained active for 15 days post-termination, and one contractor account was not included in the quarterly review scope.",
  },
  {
    id: "gitc-ct-q3",
    text: "Was GITC-03 (Change Advisory Board) operating effectively during the testing period?",
    type: "yes-no",
    answer: "Yes",
    note: "All 18 sampled changes had CAB approval prior to production deployment.",
  },
  {
    id: "gitc-ct-q4",
    text: "Rate the overall effectiveness of segregation between development and production environments (GITC-04).",
    type: "select",
    options: ["Not effective", "Partially effective", "Largely effective", "Fully effective"],
    answer: "Fully effective",
    note: "No developers have direct production access. All deployments go through the CI/CD pipeline.",
  },
  {
    id: "gitc-ct-q5",
    text: "Were any issues identified with privileged access monitoring (GITC-05)?",
    type: "yes-no",
    answer: "Yes",
    note: "Monthly CyberArk review cadence may be insufficient — two privileged sessions during month-end close were not reviewed until 6 weeks later.",
  },
];

const vendorInvoiceQuestions: MockQuestion[] = [
  {
    id: "vi-q1",
    text: "Are three-way matches (PO, receipt, invoice) required for all purchases above the materiality threshold?",
    type: "yes-no",
    answer: "Yes",
    note: "Confirmed via SAP configuration review.",
  },
  {
    id: "vi-q2",
    text: "Describe the process for handling invoice exceptions and discrepancies.",
    type: "text",
    answer:
      "Discrepancies are flagged automatically by SAP. Items within 5% tolerance are auto-approved. Exceptions above tolerance require AP supervisor review and vendor confirmation before payment release.",
  },
  {
    id: "vi-q3",
    text: "What segregation of duties controls exist in the AP process?",
    type: "multi-select",
    options: [
      "Separate invoice entry and approval",
      "Separate approval and payment",
      "Vendor master data restricted",
      "System-enforced authorization levels",
    ],
    answer: [
      "Separate invoice entry and approval",
      "Separate approval and payment",
      "Vendor master data restricted",
      "System-enforced authorization levels",
    ],
  },
  {
    id: "vi-q4",
    text: "How frequently is the AP aging report reviewed by management?",
    type: "select",
    options: ["Weekly", "Bi-weekly", "Monthly", "Quarterly"],
    answer: "Weekly",
  },
];

const journalEntryQuestions: MockQuestion[] = [
  {
    id: "je-q1",
    text: "Is dual approval required for all manual journal entries?",
    type: "yes-no",
    answer: "Yes",
    note: "System-enforced in SAP — entries cannot post without second approval.",
  },
  {
    id: "je-q2",
    text: "What automated controls exist for journal entry posting?",
    type: "multi-select",
    options: [
      "Account code validation",
      "Period posting restrictions",
      "Debit/credit balance check",
      "Duplicate entry detection",
      "Threshold-based routing",
    ],
    answer: [
      "Account code validation",
      "Period posting restrictions",
      "Debit/credit balance check",
      "Threshold-based routing",
    ],
  },
  {id: "je-q3", text: "Describe the month-end journal entry review process.", type: "text", answer: null},
];

export const PROCESS_QUESTIONNAIRES: Record<string, MockQuestionnaire[]> = {
  "proc-vendor-invoice": [
    {
      type: "external",
      questionnaire: {
        id: "vi-eq-1",
        citationId: 4,
        templateName: "SOX Controls Questionnaire",
        status: "locked",
        createdAt: new Date("2025-12-01"),
        submittedAt: new Date("2025-12-18"),
        lockedAt: new Date("2025-12-20"),
        responseCount: 5,
        totalQuestions: 5,
        questions: soxControlsQuestions,
      },
    },
    {
      type: "internal",
      questionnaire: {
        id: "vi-iq-1",
        citationId: 5,
        title: "AP Process Walkthrough Checklist",
        status: "completed",
        assignee: "Jenny Zhan",
        completedCount: 4,
        totalQuestions: 4,
        createdAt: new Date("2026-01-05"),
        completedAt: new Date("2026-01-20"),
        questions: vendorInvoiceQuestions,
      },
    },
  ],
  "proc-journal-entry": [
    {
      type: "external",
      questionnaire: {
        id: "je-eq-1",
        citationId: 5,
        templateName: "JE-01 Control Testing Questionnaire",
        status: "submitted",
        createdAt: new Date("2026-01-15"),
        submittedAt: new Date("2026-02-05"),
        lockedAt: null,
        responseCount: 4,
        totalQuestions: 4,
        questions: jeControlTestingQuestions,
      },
    },
    {
      type: "internal",
      questionnaire: {
        id: "je-iq-1",
        citationId: 6,
        title: "Journal Entry Controls Review",
        status: "in-progress",
        assignee: "James Park",
        completedCount: 2,
        totalQuestions: 3,
        createdAt: new Date("2026-02-10"),
        completedAt: null,
        questions: journalEntryQuestions,
      },
    },
  ],
};

export const ELC_QUESTIONNAIRES: Record<string, MockQuestionnaire[]> = {
  "elc-control-environment": [
    {
      type: "internal",
      questionnaire: {
        id: "ce-iq-1",
        citationId: 5,
        title: "Board Oversight Walkthrough",
        status: "completed",
        assignee: "Lisa Wang",
        completedCount: 5,
        totalQuestions: 5,
        createdAt: new Date("2026-01-12"),
        completedAt: new Date("2026-02-10"),
        questions: ceBoardWalkthroughQuestions,
      },
    },
    {
      type: "internal",
      questionnaire: {
        id: "ce-iq-2",
        citationId: 6,
        title: "Control Environment Assessment",
        status: "completed",
        assignee: "James Park",
        completedCount: 6,
        totalQuestions: 6,
        createdAt: new Date("2026-01-15"),
        completedAt: new Date("2026-02-12"),
        questions: ceEnvironmentQuestions,
      },
    },
  ],
  "elc-it-environment": [
    {
      type: "external",
      questionnaire: {
        id: "eq-1",
        citationId: 5,
        templateName: "IT Environment & GITCs Questionnaire",
        status: "locked",
        createdAt: new Date("2025-11-15"),
        submittedAt: new Date("2025-12-10"),
        lockedAt: new Date("2025-12-12"),
        responseCount: 8,
        totalQuestions: 8,
        questions: gitcQuestions,
      },
    },
    {
      type: "external",
      questionnaire: {
        id: "gitc-eq-2",
        citationId: 6,
        templateName: "GITC Control Testing Questionnaire",
        status: "submitted",
        createdAt: new Date("2026-01-10"),
        submittedAt: new Date("2026-02-01"),
        lockedAt: null,
        responseCount: 5,
        totalQuestions: 5,
        questions: gitcControlTestingQuestions,
      },
    },
  ],
  "elc-risk-assessment": [
    {
      type: "internal",
      questionnaire: {
        id: "ra-iq-1",
        citationId: 5,
        title: "Risk Assessment Process Walkthrough",
        status: "completed",
        assignee: "Lisa Wang",
        completedCount: 5,
        totalQuestions: 5,
        createdAt: new Date("2026-01-18"),
        completedAt: new Date("2026-02-15"),
        questions: [
          {
            id: "ra-iq1-q1",
            text: "Does the entity maintain a formal risk register that includes business risks relevant to financial reporting?",
            type: "yes-no",
            answer: "Yes",
            note: "Central register maintained in SharePoint; updated quarterly by risk champions across business units.",
          },
          {
            id: "ra-iq1-q2",
            text: "How frequently is the risk register reviewed and updated?",
            type: "select",
            options: ["Monthly", "Quarterly", "Semi-annually", "Annually", "Ad hoc"],
            answer: "Quarterly",
            note: "Formal quarterly review at risk committee; ad hoc updates permitted between cycles.",
          },
          {
            id: "ra-iq1-q3",
            text: "Is there a documented process for assessing the impact of significant changes on financial reporting?",
            type: "yes-no",
            answer: "Yes",
            note: "Change impact assessment template exists but not consistently applied for all change types.",
          },
          {
            id: "ra-iq1-q4",
            text: "Which risk assessment methodologies are used?",
            type: "multi-select",
            options: [
              "Qualitative likelihood/impact matrix",
              "Quantitative scoring model",
              "Scenario analysis",
              "Heat mapping",
              "Bow-tie analysis",
            ],
            answer: ["Qualitative likelihood/impact matrix", "Heat mapping"],
          },
          {
            id: "ra-iq1-q5",
            text: "Is there a formally documented and board-approved risk appetite statement?",
            type: "yes-no",
            answer: null,
          },
        ],
      },
    },
    {
      type: "internal",
      questionnaire: {
        id: "ra-iq-2",
        citationId: 6,
        title: "Change Management Risk Assessment Review",
        status: "in-progress",
        assignee: "James Park",
        completedCount: 2,
        totalQuestions: 4,
        createdAt: new Date("2026-02-01"),
        completedAt: null,
        questions: [
          {
            id: "ra-iq2-q1",
            text: "Were all significant business changes during the period subject to a formal risk assessment?",
            type: "yes-no",
            answer: "No",
            note: "Customer loss and WMS migration were not formally assessed for financial reporting impact.",
          },
          {
            id: "ra-iq2-q2",
            text: "How are emerging risks identified and communicated to the risk committee?",
            type: "text",
            answer:
              "Regulatory changes via external monitoring service. Operational changes rely on business unit escalation — no systematic scanning process.",
          },
          {
            id: "ra-iq2-q3",
            text: "Are risk responses tracked to completion with documented evidence?",
            type: "yes-no",
            answer: null,
          },
          {
            id: "ra-iq2-q4",
            text: "Is the risk register reconciled to the financial reporting risk assessment performed by the audit team?",
            type: "yes-no",
            answer: null,
          },
        ],
      },
    },
  ],
  "elc-monitoring-governance": [
    {
      type: "internal",
      questionnaire: {
        id: "moc-iq-1",
        citationId: 6,
        title: "Control Monitoring Effectiveness Review",
        status: "in-progress",
        assignee: "Lisa Wang",
        completedCount: 4,
        totalQuestions: 5,
        createdAt: new Date("2026-01-20"),
        completedAt: null,
        questions: mocMonitoringEffectivenessQuestions,
      },
    },
    {
      type: "internal",
      questionnaire: {
        id: "moc-iq-2",
        citationId: 7,
        title: "Deficiency Remediation Tracking Review",
        status: "in-progress",
        assignee: "James Park",
        completedCount: 3,
        totalQuestions: 5,
        createdAt: new Date("2026-02-01"),
        completedAt: null,
        questions: mocDeficiencyTrackingQuestions,
      },
    },
  ],
};
